import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { jsonFetch } from "@/lib/json-fetch";
import {
  atlasSuggestKeys,
  dashboardHeatmapKeys,
  resourceDirectoryKeys,
  solutionDashboardKeys,
  solutionDetailKeys,
  staffingRequestKeys,
  staffingRequestPriorityKeys,
} from "@/lib/query";
import type { StaffingRequestPriorityView } from "@/schemas/staffing-request-priorities";
import type {
  CreateStaffingRequestInput,
  OpenRolePlanGroup,
  StaffingRequestDetailView,
  StaffingRequestKind,
  StaffingRequestPage,
  StaffingRequestStatus,
  StaffingRequestView,
  StaffingResourceOption,
  StaffingSolutionOption,
  StaffingSwapCandidate,
} from "@/schemas/staffing-requests";

export type { StaffingRequestPriorityView };

// ── Queue + detail ───────────────────────────────────────────────────────────

export interface QueueParams {
  statuses?: StaffingRequestStatus[];
  kinds?: StaffingRequestKind[];
  priorityIds?: string[];
  cursor?: string;
  direction?: "next" | "prev";
  limit?: number;
}

export function useStaffingRequests(params: QueueParams) {
  return useQuery({
    queryKey: staffingRequestKeys.list(params),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params.statuses?.length) qs.set("status", params.statuses.join(","));
      if (params.kinds?.length) qs.set("kind", params.kinds.join(","));
      if (params.priorityIds?.length) qs.set("priorityIds", params.priorityIds.join(","));
      if (params.cursor) qs.set("cursor", params.cursor);
      if (params.direction) qs.set("direction", params.direction);
      if (params.limit) qs.set("limit", String(params.limit));
      return jsonFetch<StaffingRequestPage>(`/api/staffing-requests?${qs.toString()}`);
    },
    placeholderData: keepPreviousData,
  });
}

export function useStaffingRequest(id: string) {
  return useQuery({
    queryKey: staffingRequestKeys.detail(id),
    queryFn: () => jsonFetch<StaffingRequestDetailView>(`/api/staffing-requests/${id}`),
  });
}

// ── Form options ─────────────────────────────────────────────────────────────

export function useStaffingSolutionOptions(enabled: boolean) {
  return useQuery({
    queryKey: staffingRequestKeys.solutionOptions(),
    queryFn: async () =>
      (
        await jsonFetch<{ solutions: StaffingSolutionOption[] }>(
          "/api/staffing-requests/solution-options",
        )
      ).solutions,
    enabled,
  });
}

export function useStaffingResourceOptions(solutionId: string | null, search: string) {
  return useQuery({
    queryKey: staffingRequestKeys.resourceOptions(solutionId ?? "", search),
    queryFn: async () => {
      const qs = new URLSearchParams({ solutionId: solutionId ?? "", search });
      return (
        await jsonFetch<{ resources: StaffingResourceOption[] }>(
          `/api/staffing-requests/resource-options?${qs.toString()}`,
        )
      ).resources;
    },
    enabled: solutionId !== null,
    placeholderData: keepPreviousData,
  });
}

/** People who could be swapped into a seat on the Solution, with their seats on other Pods. */
export function useSwapCandidates(solutionId: string | null, search: string) {
  return useQuery({
    queryKey: staffingRequestKeys.swapCandidates(solutionId ?? "", search),
    queryFn: async () => {
      const qs = new URLSearchParams({ solutionId: solutionId ?? "", search });
      return (
        await jsonFetch<{ candidates: StaffingSwapCandidate[] }>(
          `/api/staffing-requests/swap-candidates?${qs.toString()}`,
        )
      ).candidates;
    },
    enabled: solutionId !== null,
    placeholderData: keepPreviousData,
  });
}

/** Every plan's open roles, grouped by plan. Refreshed with the rest of the page. */
export function useOpenRolesByPlan() {
  return useQuery({
    queryKey: staffingRequestKeys.openRoles(),
    queryFn: async () =>
      (await jsonFetch<{ plans: OpenRolePlanGroup[] }>("/api/staffing-requests/open-roles")).plans,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────

function postJson<T>(url: string, body: unknown): Promise<T> {
  return jsonFetch<T>(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function useCreateStaffingRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStaffingRequestInput) =>
      postJson<StaffingRequestView>("/api/staffing-requests", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffingRequestKeys.all });
      toast.success("Staffing request sent to Resource Management");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/** Approval changes the Pod, so every view that shows staffing refreshes too. */
export function useApproveStaffingRequest(request: StaffingRequestView) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { resourceId: string | null; note?: string }) =>
      postJson<StaffingRequestDetailView>(`/api/staffing-requests/${request.id}/approve`, input),
    onSuccess: () => {
      const solutionId = request.solution.id;
      qc.invalidateQueries({ queryKey: staffingRequestKeys.all });
      qc.invalidateQueries({ queryKey: solutionDetailKeys.detail(solutionId) });
      // A Swap also changes the Pod it moved someone off.
      const sourceSolutionId = request.source?.solution?.id;
      if (sourceSolutionId) {
        qc.invalidateQueries({ queryKey: solutionDetailKeys.detail(sourceSolutionId) });
      }
      qc.invalidateQueries({ queryKey: solutionDashboardKeys.all });
      qc.invalidateQueries({ queryKey: resourceDirectoryKeys.all });
      qc.invalidateQueries({ queryKey: dashboardHeatmapKeys.all });
      qc.invalidateQueries({ queryKey: atlasSuggestKeys.forSolution(solutionId) });
      toast.success("Approved — the Pod has been updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useRejectStaffingRequest(requestId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      postJson<StaffingRequestDetailView>(`/api/staffing-requests/${requestId}/reject`, {
        reason,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffingRequestKeys.all });
      toast.success("Request rejected");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useWithdrawStaffingRequest(requestId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      postJson<StaffingRequestView>(`/api/staffing-requests/${requestId}/withdraw`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffingRequestKeys.all });
      toast.success("Request withdrawn");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// ── Priorities (Settings ▸ Request Priorities) ───────────────────────────────

export function useStaffingRequestPriorities() {
  return useQuery({
    queryKey: staffingRequestPriorityKeys.list(),
    queryFn: async (): Promise<StaffingRequestPriorityView[]> => {
      const data = await jsonFetch<{ priorities: StaffingRequestPriorityView[] }>(
        "/api/staffing-request-priorities",
      );
      return data.priorities;
    },
  });
}

export interface StaffingRequestPriorityInput {
  name: string;
  description?: string | null;
  active?: boolean;
  sortOrder?: number;
}

export function useCreateStaffingRequestPriority() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StaffingRequestPriorityInput) =>
      postJson<StaffingRequestPriorityView>("/api/admin/staffing-request-priorities", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffingRequestPriorityKeys.all });
      toast.success("Request Priority created");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdateStaffingRequestPriority() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: Partial<StaffingRequestPriorityInput> & { id: string }) =>
      jsonFetch<StaffingRequestPriorityView>(`/api/admin/staffing-request-priorities/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffingRequestPriorityKeys.all });
      // Renames and re-ranking show on request rows and details.
      qc.invalidateQueries({ queryKey: staffingRequestKeys.all });
      toast.success("Request Priority updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
